import styled from "styled-components/macro";
import { dropdownZIndex, fieldsBorderRadius } from "../constants";
import { boxShadow } from "../GlobalStyle";
import { SelectStyleProps } from "./helpers";
import { SelectDropdownMaxHeight } from "../variables";
import { radius, spacing } from "../../Foundation";

// Colours are not in theme
export const activeSelect = "#F0F9FC";

export interface StateTypes {
    isFocused: boolean;
    isSelected: boolean;
    isDisabled: boolean;
    isHighlight?: boolean;
    isMulti?: boolean;
    selectProps: {
        dropdownColor: string;
        menuIsOpen: boolean;
    };
}

interface SelectDropdownStyleProps extends SelectStyleProps {
    dropdownMenuHeight?: string;
    ismenuIsOpen?: boolean;
}

export const PlaceHolder = styled.div`
    color: ${(props): string =>
        props.color ? props.color : props.theme.colors.neutral.dark.backgroundColor};
    font-weight: 400;
    letter-spacing: 0.3px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

export const indicatorSeparatorStyle = {
    alignSelf: "stretch",
    backgroundColor: "#c0c0c0",
    marginBottom: spacing.spacing_8,
    marginTop: spacing.spacing_8,
    width: 1
};

export const customStyles = {
    control: (provided: object): object => ({
        ...provided,
        "&:hover": {
            border: "1px solid #4692A9",
            boxShadow: "none"
        },
        border: "1px solid #BFBFBF",
        borderRadius: fieldsBorderRadius,
        boxShadow: "none"
    }),
    dropdownIndicator: (provided: object, state: StateTypes): object => ({
        ...provided,
        color: state.selectProps.dropdownColor ? state.selectProps.dropdownColor : "black",
        transform: state.isFocused ? "rotate(0deg)" : "rotate(0deg)"
    }),
    indicatorSeparator: (provided: object): object => ({
        ...provided,
        backgroundColor: "none"
    }),
    input: (provided: object): object => ({
        ...provided,
        color: "#6D6E71"
    }),
    menu: (provided: object): object => ({
        ...provided,
        display: "flex",
        flexDirection: "column"
    }),
    menuList: (provided: object): object => ({
        ...provided,
        display: "flex",
        flexDirection: "column",
        maxHeight: SelectDropdownMaxHeight
    }),
    menuPortal: (provided: object): object => ({
        ...provided,
        zIndex: dropdownZIndex
    }),
    option: (provided: object, state: StateTypes): object => {
        let background = "none";

        if (state.isSelected) {
            background = activeSelect;
        }

        return {
            ...provided,
            "&:hover": {
                backgroundColor: "white",
                color: state.isDisabled ? "#CCCCCC" : "#4692A9",
                cursor: state.isDisabled ? "not-allowed" : "pointer",
                fontWeight: 500
            },
            ".default-scenario-option": {
                "&::after": {
                    background: "#000",
                    bottom: 0,
                    content: "''",
                    height: 1,
                    left: 10,
                    position: "absolute",
                    top: 65,
                    width: "calc(100% - 20px)"
                },

                "&:hover": {
                    background: "#E1EDF1",
                    color: "#4692A9",
                    fontWeight: 700
                },

                backgroundColor: "#d7d8d9",
                borderRadius: radius.radius_8,
                color: "black",
                cursor: "pointer",
                fontWeight: "700",
                padding: spacing.spacing_10
            },

            background,

            // Colours are not in theme
            // eslint-disable-next-line no-nested-ternary
            color: state.isSelected ? "#4D4D4D" : state.isDisabled ? "#CCCCCC" : "#595959",
            cursor: state.isDisabled ? "not-allowed" : "pointer",
            fontSize: "16px",
            fontWeight: 500
        };
    },
    placeholder: (provided: object): object => ({
        ...provided,
        display: "flex",
        fontSize: 16,
        justifyContent: "start",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        width: "100%"
    })
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const MultiSelectWrapper = styled.div<any>`
    outline: none;

    hr {
        margin: 0 0 ${spacing.spacing_8} 0;
        border-top: #9db4e0 1px solid;
    }

    .custom-multiselect__menu {
        display: flex;
        flex-direction: column;
        overflow-y: auto;
        width: 100%;
        border: 1px solid
            ${(props: SelectStyleProps): string => props.theme.colors.primary.main.backgroundColor};
        margin: ${spacing.spacing_2} 0 0 0;
        box-shadow: ${boxShadow};
        z-index: 9;
        border-radius: ${fieldsBorderRadius};
        max-height: ${(props: SelectDropdownStyleProps) =>
            props.dropdownMenuHeight ? props.dropdownMenuHeight : `${SelectDropdownMaxHeight}px`};
    }

    .custom-multiselect__indicator {
        transform: ${(props: SelectDropdownStyleProps): string =>
            props.ismenuIsOpen ? "rotate(180deg)" : "rotate(0deg)"};
        transition: transform 0.3s ease-in-out;
    }

    .custom-multiselect__menu-list {
        padding: ${spacing.spacing_4};
    }
`;

export const MultiSelectBottomHeader = styled.div`
    display: flex;
    justify-content: space-between;
    border-top: 1px solid #ccc;
    padding: ${spacing.spacing_10};

    .select-all {
        color: black;
    }

    .clear-all {
        color: red;
    }

    .select-all,
    .clear-all {
        cursor: pointer;
        border: none;
        background-color: transparent;
        padding: 0 ${spacing.spacing_4};
        font-weight: 500;

        &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }
    }
`;

export const GroupContainer = styled.div`
    padding: 0;
    position: relative;
`;

export const DropdownSubmenu = styled.div`
    padding: ${spacing.spacing_4};
    display: flex;
    flex-direction: column;
    gap: ${spacing.spacing_4};
`;

interface SubDropdownContainerProps {
    isOpened: boolean;
}

export const SubDropdownContainer = styled.div<SubDropdownContainerProps>`
    background-color: ${(props): string => (props.isOpened ? "#F3F4F6" : "transparent")};
    border-radius: ${radius.radius_10};
    padding: 0 ${spacing.spacing_2};

    .dropdown-submenu {
        padding: ${spacing.spacing_4};
        display: flex;
        flex-direction: column;
        gap: ${spacing.spacing_4};
    }
`;
